import { Alert, Box, Button, CircularProgress, Typography, Paper, DialogActions, Link } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useState } from "react";
import { enqueueSnackbar } from "notistack";
import { TPagamentoCartaoParams } from "./index";

export default function TefForm({ tefConfig, pagamentoId, valor, onClose, onReceberPagamento, setManual }: TefFormProps) {
    const { setValue } = useFormContext();

    const [statusTef, setStatusTef] = useState<StatusTef>('inicial');
    const [carregando, setCarregando] = useState(false);

    const iniciarTef = async () => {
        setCarregando(true);
        setStatusTef('processando');

        const extrairDadosRecibo = (cardholderReceipt: string[]) => {
            const linhaComDados = cardholderReceipt.find(linha =>
                linha.includes('NSU=') && linha.includes('AUT=')
            );

            if (!linhaComDados) {
                return { nsu: '', autorizacao: '' };
            }

            // Regex para extrair NSU e AUT
            const nsuMatch = linhaComDados.match(/NSU=(\d+)/);
            const autMatch = linhaComDados.match(/AUT=(\d+)/);

            return {
                nsu: nsuMatch ? nsuMatch[1] : '',
                autorizacao: autMatch ? autMatch[1] : ''
            };
        };

        try {
            enqueueSnackbar("TEF iniciado! Verifique a máquina.", { variant: "info" });

            // 1. Init TEF
            const initResponse = await fetch('/tef-proxy/v1/pinpad/init', {
                method: 'POST',
                headers: {
                    'Authorization': 'mk_ZcHzbH65cUG8Xb5ozShw',
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                },
                body: JSON.stringify({
                    applicationName: "PDV Techpines",
                    applicationVersion: "1.0.0",
                    activationCode: tefConfig.cod_ativacao,
                    pinpadMessages: {
                        initializationMessage: "Iniciando...",
                        mainMessage: "Techpines",
                        processingMessage: "Enviando..."
                    }
                })
            });

            if (!initResponse.ok) {
                throw new Error(`Init falhou: ${initResponse.status}`);
            }

            // 2. Iniciar Charge (não esperar resposta completa)
            enqueueSnackbar("Processando... Insira o cartão na máquina", { variant: "info" });

            fetch('/tef-proxy/v1/charge/authorization', {
                method: 'POST',
                headers: {
                    'Authorization': 'mk_ZcHzbH65cUG8Xb5ozShw',
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                },
                body: JSON.stringify({
                    amount: Math.round(valor * 100),
                    paymentType: "Credit",
                    installmentType: "None"
                })
            });

            // 3. Polling - verificar status a cada 2 segundos
            const interval = setInterval(async () => {
                try {
                    const statusResponse = await fetch('/tef-proxy/v1/charge/authorization', {
                        method: 'POST',
                        headers: {
                            'Authorization': 'mk_ZcHzbH65cUG8Xb5ozShw',
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            amount: Math.round(valor * 100),
                            paymentType: "Credit",
                            installmentType: "None"
                        })
                    });

                    if (statusResponse.ok) {
                        const data = await statusResponse.json();
                        console.log('📊 Status TEF:', data);

                        if (data.status === 'FINISHED') {
                            clearInterval(interval);

                            if (data.isApproved && data.charge) {
                                // Extrair NSU e AUT do cardholderReceipt
                                const { nsu, autorizacao } = extrairDadosRecibo(data.charge.cardholderReceipt || []);

                                enqueueSnackbar("TEF processado com sucesso!", { variant: "success" });

                                // Chamar diretamente a mutation de receber pagamento
                                if (onReceberPagamento) {
                                    const params: TPagamentoCartaoParams = {
                                        valor: valor,
                                        nsu: nsu,
                                        autorizacao: autorizacao
                                    };
                                    onReceberPagamento(pagamentoId, params);
                                }

                                // Fechar o modal após processar
                                onClose();
                            } else {
                                // Negado
                                setStatusTef('erro');
                                enqueueSnackbar('Pagamento negado ou cancelado', { variant: "error" });
                            }
                            setCarregando(false);
                        }
                    }
                } catch (pollingError) {
                    console.log('Polling error:', pollingError);
                }
            }, 2000);

            // Timeout de 5 minutos
            setTimeout(() => {
                clearInterval(interval);
                if (statusTef === 'processando') {
                    setStatusTef('erro');
                    setCarregando(false);
                    enqueueSnackbar("Timeout: Operação demorou muito", { variant: "error" });
                }
            }, 300000);

        } catch (error: any) {
            console.error('❌ Erro TEF:', error);
            setStatusTef('erro');
            setCarregando(false);
            enqueueSnackbar(`Erro no TEF: ${error.message}`, { variant: "error" });
        }
    };

    const tentarNovamente = () => {
        setStatusTef('inicial');
        setValue('nsu', '');
        setValue('autorizacao', '');
    };

    return (
        <Box>
            {/* Informações do TEF */}
            <Alert severity="info" sx={{ mb: 2 }} icon={false}>
                <Typography variant="subtitle2" gutterBottom>
                    Configuração TEF encontrada:
                </Typography>
                <Typography variant="body2">
                    <strong>{tefConfig.nome}</strong> - {tefConfig.loja.nome}
                </Typography>
            </Alert>

            {/* Estado Inicial */}
            {statusTef === 'inicial' && (
                <Paper
                    sx={{
                        border: '2px dashed #e0e0e0',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: '#fafafa',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'rgba(76, 175, 80, 0.04)',
                        }
                    }}
                    onClick={iniciarTef}
                    elevation={0}
                >
                    <Typography variant="h6" color="primary" gutterBottom>
                        💳 Processamento TEF
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        Clique para iniciar o pagamento via TEF
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ mt: 2 }}
                        disabled={carregando}
                    >
                        INICIAR TEF
                    </Button>
                </Paper>
            )}

            {/* Estado Processando */}
            {statusTef === 'processando' && (
                <Box textAlign="center" py={4}>
                    <CircularProgress size={60} sx={{ mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Processando TEF...
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Aguarde o processamento na máquina TEF
                    </Typography>
                </Box>
            )}



            {/* Estado Erro */}
            {statusTef === 'erro' && (
                <Box>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            ❌ Erro no processamento TEF
                        </Typography>
                        <Typography variant="body2">
                            Houve um problema durante o processamento.
                        </Typography>
                    </Alert>
                    <Box textAlign="center" sx={{ mb: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={tentarNovamente}
                        >
                            Tentar Novamente
                        </Button>
                    </Box>
                    <Box textAlign="center">
                        <Link
                            onClick={setManual}
                            sx={{ cursor: 'pointer' }}
                        >
                            Inserir manualmente
                        </Link>
                    </Box>
                </Box>
            )}

            {/* DialogActions */}
            <DialogActions sx={{ pt: 2 }}>
                {(statusTef === 'inicial' || statusTef === 'erro') && (
                    <Button onClick={onClose} color="primary">
                        Cancelar
                    </Button>
                )}
            </DialogActions>
        </Box>
    );
}

interface TefFormProps {
    tefConfig: {
        id: number;
        nome: string;
        cod_ativacao: string;
        autorizacao: string;
        loja: {
            id: number;
            nome: string;
        };
    };
    pagamentoId: number;
    valor: number;
    onClose: () => void;
    onSubmit: (data: any) => void;
    onReceberPagamento?: (id: number, data: TPagamentoCartaoParams | null) => void;
    isEdit: boolean;
    setManual: () => void;
}

type StatusTef = 'inicial' | 'processando' | 'erro';

